import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
} from "@mui/material";
import { contactService } from "../services/api";
import ImageIcon from "@mui/icons-material/Image";
import EditIcon from "@mui/icons-material/Edit";
import CampaignIcon from "@mui/icons-material/Campaign";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    workEmail: "",
    company: "",
    requirements: "",
    agreeToTerms: false,
  });
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "agreeToTerms" ? checked : value,
    }));
    // Clear error when user starts typing
    if (status.error) {
      setStatus((prev) => ({ ...prev, error: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.agreeToTerms) {
      setStatus({
        submitted: false,
        submitting: false,
        error: "Please agree to the Terms of Service and Privacy Policy",
      });
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setStatus({
        submitted: false,
        submitting: false,
        error: "Please provide both first and last name",
      });
      return;
    }

    if (!formData.workEmail) {
      setStatus({
        submitted: false,
        submitting: false,
        error: "Please provide your work email",
      });
      return;
    }

    if (!formData.requirements) {
      setStatus({
        submitted: false,
        submitting: false,
        error: "Please tell us how we can help you",
      });
      return;
    }

    setStatus({ submitted: false, submitting: true, error: null });

    try {
      await contactService.submitForm(formData);
      setStatus({
        submitted: true,
        submitting: false,
        error: null,
      });
      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        workEmail: "",
        company: "",
        requirements: "",
        agreeToTerms: false,
      });
    } catch (error) {
      setStatus({
        submitted: false,
        submitting: false,
        error: error.message || "Failed to submit form. Please try again.",
      });
    }
  };

  const features = [
    {
      icon: <ImageIcon sx={{ fontSize: 40, color: "#BF2924" }} />,
      title: "Accelerate Insight Generation Instantly",
      description:
        "Say goodbye to manual data processing and slow analytical workflows. With Arcogni’s autonomous AI engine, you can transform raw data into meaningful insights, clear visualizations, and structured outputs within seconds — empowering faster and more informed scientific and engineering decisions.",
    },
    {
      icon: <EditIcon sx={{ fontSize: 40, color: "#BF2924" }} />,
      title: "Generate Research-Ready Algorithms Effortlessly",
      description:
        "Arcogni helps you create high-quality algorithms and analytical models without the complexity of manual coding. Whether you are developing simulations, running experiments, or analyzing large datasets, Arcogni delivers precise, AI-driven outputs that accelerate scientific and engineering progress.",
    },
    {
      icon: <CampaignIcon sx={{ fontSize: 40, color: "#BF2924" }} />,
      title: "Streamline and Enhance Your R&D Workflow",
      description:
        "Stay organized with Arcogni’s unified research and analysis framework. Designed to optimize scientific and engineering operations, the platform centralizes data, models, and insights — improving efficiency, reducing redundancy, and enabling seamless collaboration across teams.",
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
      <Typography
        variant="h2"
        align="center"
        gutterBottom
        sx={{ fontWeight: "bold", mb: 2 }}
      >
        We'd Love to Hear From You
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color="text.secondary"
        sx={{ mb: 6, maxWidth: 800, mx: "auto" }}
      >
        Got questions, feedback, or ideas? Our team is ready to help you get the
        most out of GenAI. Whether you're exploring AI tools or need assistance
        with your account—we're just a message away.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {features.map((feature, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: "transparent",
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  {feature.icon}
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Fill Out this Quick Form:
            </Typography>

            {status.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {status.error}
              </Alert>
            )}

            {status.submitted ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Thank you for your message! We'll get back to you soon.
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name*"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name*"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Work Email*"
                      name="workEmail"
                      type="email"
                      value={formData.workEmail}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company*"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="How Can We Help You*"
                      name="requirements"
                      multiline
                      rows={4}
                      value={formData.requirements}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="agreeToTerms"
                          checked={formData.agreeToTerms}
                          onChange={handleChange}
                          disabled={status.submitting}
                          sx={{
                            color: "#BF2924",
                            "&.Mui-checked": {
                              color: "#BF2924",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          By checking the box you agree to our{" "}
                          <Link
                            href="/terms"
                            color="primary"
                            sx={{ textDecoration: "none" }}
                          >
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link
                            href="/privacy"
                            color="primary"
                            sx={{ textDecoration: "none" }}
                          >
                            Privacy Policy
                          </Link>
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 3,
                    py: 1.5,
                    backgroundColor: "#BF2924",
                    "&:hover": {
                      backgroundColor: "#a61f1b",
                    },
                  }}
                  disabled={status.submitting || !formData.agreeToTerms}
                >
                  {status.submitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Submit Message"
                  )}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Contact;
